import type {
  AIDecompositionResult, NodeType, RelationType, EpistemicKind, Provenance,
} from "@/types";

// a deterministic, plausible decomposition from pasted prose.
// used when the real gpt-5.5 backend is unreachable so the create flow
// always works in a demo. it mirrors the real prompt's grammar:
//   - a reasoning CHAIN (evidence → interpretation → thesis), not a star
//   - caveats emitted as docked modifiers (attachment_kind), not peer nodes
//   - hedged trailing sentences become thesis VARIANTS, not extra nodes
//   - a hidden warrant undercutting an inference (edge), not a node

const HEDGE_WORDS = [
  "probably", "likely", "seems", "maybe", "might", "assume", "assumed",
  "implicitly", "really", "honestly", "basically", "essentially",
  "the thing is", "the real", "the whole",
];

const EVIDENCE_WORDS = [
  "study", "studies", "data", "evidence", "research", "found", "shows",
  "percent", "%", "report", "survey", "statistics", "thousands", "half",
];

const VALUE_WORDS = [
  "should", "ought", "better", "worse", "good", "bad", "right", "wrong",
  "deserve", "fair", "unfair", "matters", "value",
];

const VARIANT_OPENERS = /^(maybe|perhaps|or maybe|arguably|honestly,? maybe)\b/i;

function splitSentences(text: string): string[] {
  return text
    .replace(/\s+/g, " ")
    .split(/(?<=[.!?])\s+(?=[A-Z"'(])/)
    .map((s) => s.trim())
    .filter((s) => s.length > 12);
}

function detectType(sentence: string): { type: NodeType; kind: EpistemicKind; provenance: Provenance } {
  const lower = sentence.toLowerCase();
  if (EVIDENCE_WORDS.some((w) => lower.includes(w))) {
    return { type: "empirical_claim", kind: "empirical", provenance: "user" };
  }
  if (VALUE_WORDS.some((w) => lower.includes(w))) {
    return { type: "value_claim", kind: "normative", provenance: "user" };
  }
  if (HEDGE_WORDS.some((w) => lower.includes(w))) {
    return { type: "hidden_warrant", kind: "causal", provenance: "ai_inferred" };
  }
  return { type: "claim", kind: "evaluative", provenance: "user" };
}

function titleFrom(text: string): string {
  const first = splitSentences(text)[0] ?? text.trim();
  const words = first.replace(/[.!?]+$/, "").split(/\s+/);
  if (words.length <= 9) return first.replace(/[.!?]+$/, "");
  return words.slice(0, 9).join(" ") + "…";
}

function summaryFrom(text: string): string {
  const sentences = splitSentences(text);
  if (sentences.length <= 2) return text.trim().slice(0, 180);
  return sentences.slice(0, 2).join(" ");
}

export function mockDecompose(text: string): AIDecompositionResult {
  const sentences = splitSentences(text);
  const cleaned = sentences.length ? sentences : [text.trim()];

  const nodes: AIDecompositionResult["nodes"] = [];
  const edges: AIDecompositionResult["edges"] = [];
  const thesis_variants: NonNullable<AIDecompositionResult["thesis_variants"]> = [];

  // first sentence becomes the thesis, positioned as the right-side endpoint
  const thesisText = cleaned[0];
  nodes.push({
    temp_id: "n1",
    type: "claim",
    epistemic_kind: "evaluative",
    text: thesisText.replace(/[.!?]+$/, "") + ".",
    original_text: thesisText,
    provenance: "user",
    status: "contested",
    flags: ["thesis", "contested"],
    position: { x: 1050, y: 280 },
  });

  // hedged trailing sentences ("Maybe X is the better bet…") read as
  // alternative wordings of the thesis, not extra graph nodes
  const body: string[] = [];
  for (const sentence of cleaned.slice(1, 8)) {
    if (VARIANT_OPENERS.test(sentence) && thesis_variants.length < 2) {
      thesis_variants.push({
        intent: "reframe",
        text: sentence.replace(/[.!?]+$/, "") + ".",
        note: "Your prose hedges toward this alternative wording of the claim.",
      });
    } else {
      body.push(sentence);
    }
  }

  // evidence-flavored sentences become foundations feeding one inferred
  // interpretation; everything else connects to the thesis directly
  const evidenceIds: string[] = [];
  let firstSupportEdgeId: string | null = null;

  body.slice(0, 6).forEach((sent, i) => {
    const { type, kind, provenance } = detectType(sent);
    const tempId = `n${i + 2}`;
    const isEvidence = type === "empirical_claim";
    nodes.push({
      temp_id: tempId,
      type,
      epistemic_kind: kind,
      text: sent.replace(/[.!?]+$/, "") + ".",
      original_text: provenance === "ai_inferred" ? undefined : sent,
      provenance,
      confidence: provenance === "ai_inferred" ? 0.6 + (i % 3) * 0.1 : undefined,
      status: provenance === "ai_inferred" ? "unconfirmed" : isEvidence ? "needs_evidence" : "active",
      flags: provenance === "ai_inferred" ? ["inferred"] : isEvidence ? ["needs evidence"] : [],
      position: { x: 60, y: 90 + i * 190 },
    });

    if (isEvidence) {
      evidenceIds.push(tempId);
      return; // wired to the interpretation below
    }

    const lower = sent.toLowerCase();
    const isRebuttal = /\b(but|however|though|against|wrong|fails|doesn't|does not|problem)\b/.test(lower);
    const relation: RelationType = isRebuttal ? "rebuts" : "supports";
    const edgeId = `e${i + 1}`;
    edges.push({
      temp_id: edgeId,
      source: tempId,
      target: "n1",
      relation,
      label: isRebuttal ? "objection" : "reason",
      confidence: provenance === "ai_inferred" ? 0.6 : undefined,
    });
    if (!isRebuttal && !firstSupportEdgeId) firstSupportEdgeId = edgeId;
  });

  // intermediate inference: what the evidence implies for the thesis
  if (evidenceIds.length > 0) {
    nodes.push({
      temp_id: "n_interp",
      type: "interpretation",
      epistemic_kind: "causal",
      text: "Taken together, the evidence points at the thesis only if it generalizes beyond the cited cases.",
      provenance: "ai_inferred",
      confidence: 0.6,
      status: "unconfirmed",
      flags: ["inferred"],
      position: { x: 560, y: 240 },
    });
    evidenceIds.forEach((id, i) => {
      edges.push({
        temp_id: `e_ev${i + 1}`,
        source: id,
        target: "n_interp",
        relation: "evidences",
        label: "feeds the inference",
        confidence: 0.65,
      });
    });
    edges.push({
      temp_id: "e_interp",
      source: "n_interp",
      target: "n1",
      relation: "supports",
      label: "what the evidence implies",
      confidence: 0.6,
    });
    firstSupportEdgeId = firstSupportEdgeId ?? "e_interp";
  }

  // hidden warrant attacking the load-bearing inference — an edge objection,
  // never a node attack
  if (firstSupportEdgeId) {
    nodes.push({
      temp_id: "n_hidden",
      type: "hidden_warrant",
      epistemic_kind: "causal",
      text: "This step assumes the pattern is causal rather than a selection effect — the strongest objections will land here.",
      provenance: "ai_inferred",
      confidence: 0.55,
      status: "unconfirmed",
      flags: ["inferred", "crux"],
      position: { x: 560, y: 520 },
    });
    edges.push({
      temp_id: "e_hidden",
      source: "n_hidden",
      target_edge: firstSupportEdgeId,
      relation: "undercuts",
      label: "attacks the inference",
      confidence: 0.55,
    });
  }

  // one inferred caveat docked to the thesis, so the modifier grammar is
  // visible in every demo decomposition
  nodes.push({
    temp_id: "n_caveat",
    type: "scope_condition",
    epistemic_kind: "empirical",
    text: "This claim is about the typical case; the strongest counterexamples live at the edges of the population.",
    provenance: "ai_inferred",
    confidence: 0.55,
    status: "unconfirmed",
    flags: ["inferred"],
    attachment_kind: "caveat",
    position: { x: 1050, y: 520 },
  });
  edges.push({
    temp_id: "e_caveat",
    source: "n_caveat",
    target: "n1",
    relation: "qualifies",
    label: "caveat",
    confidence: 0.55,
  });

  return {
    title: titleFrom(text),
    summary: summaryFrom(text),
    possible_theses: cleaned.length > 3
      ? [{ text: thesisText, reason: "The opening sentence bundles the main claim; you may want to split it if it actually argues several things." }]
      : [],
    thesis_variants,
    nodes,
    edges,
    questions_for_user: [
      "Is the first sentence your real thesis, or does it bundle several claims that should be separate strains?",
      "What would you count as evidence against this idea?",
    ],
    likely_cruxes: [
      { node_temp_id: "n_hidden", reason: "The causal step between the evidence and the thesis is doing load-bearing work — most forks would diverge here." },
    ],
  };
}
